// src/utils/api.js — Axios instance with JWT auto-refresh
import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

// Attach access token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('njjba_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-refresh on 401
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => error ? prom.reject(error) : prom.resolve(token));
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 &&
        error.response?.data?.code === 'TOKEN_EXPIRED' &&
        !originalRequest._retry) {

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post('/api/auth/staff/refresh', {}, { withCredentials: true });
        const { accessToken } = response.data;
        localStorage.setItem('njjba_access_token', accessToken);
        processQueue(null, accessToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('njjba_access_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // [marker: books-pro v1] closed period: a super admin may confirm and resend; everyone else sees the message
    if (error.response?.status === 423 && error.response?.data?.canOverride && !originalRequest._closedRetry) {
      const ok = window.confirm(`${error.response.data.error}\n\nYou’re a super admin, so you can make this change anyway. It will be recorded in the Audit Trail.\n\nChange the closed period?`);
      if (ok) {
        originalRequest._closedRetry = true;
        if (originalRequest.headers && typeof originalRequest.headers.set === 'function') originalRequest.headers.set('X-Closed-Override', '1');
        else originalRequest.headers = { ...(originalRequest.headers || {}), 'X-Closed-Override': '1' };
        return api(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
